import type { AppInventoryState } from '@openx/types/targeting/appInventory';
import { AppInventoryOption, type AppInventoryOptionType } from '@openx/types/targeting/appInventory';
import { AppInventoryType, InventoryContentOption } from '@openx/types/targeting/inventoryAndContent';
import type { TargetingCustomOptions } from '@openx/types/targeting/targeting';
import { type ComparisonMap, ComparisonType } from '@openx/types/targeting/targetingValuesTypes';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';
import {
  deriveBundleIdComparisonType,
  deriveComparisonType,
} from '../../../InventoryAndContent/InventoryAndContentItems/utils';
import { useAppInventoryAllowed } from '../../../shared/InventoryAndContent/featureFlags';

export type UseAppInventoryStateProps<TContentOption extends AppInventoryOptionType | InventoryContentOption> = {
  topBoxCustomOptions: TargetingCustomOptions<TContentOption> | undefined;
  initState: AppInventoryState;
};

export type UseAppInventoryStateReturn<TContentOption extends AppInventoryOptionType | InventoryContentOption> = {
  setAppNameComparisonType: Dispatch<SetStateAction<ComparisonType>>;
  setAppInventoryType: Dispatch<SetStateAction<AppInventoryType>>;
  setContentOption: Dispatch<SetStateAction<TContentOption>>;
  setBundleIdComparisonType: Dispatch<SetStateAction<ComparisonType>>;
  isAppInventoryAllowed: boolean;
  contentOption: TContentOption;
  appInventoryType: AppInventoryType;
  appNameComparisonType: ComparisonType;
  bundleIdComparisonType: ComparisonType;
};

export const useAppInventoryState = <TContentOption extends AppInventoryOptionType | InventoryContentOption>({
  topBoxCustomOptions,
  initState,
}: UseAppInventoryStateProps<TContentOption>): UseAppInventoryStateReturn<TContentOption> => {
  const isAppInventoryAllowed = useAppInventoryAllowed();

  // When App Inventory is enabled, use it as default instead of APP_BUNDLE_ID
  const getDefaultContentOption = (): TContentOption => {
    const defaultOption =
      topBoxCustomOptions?.defaultOption ??
      (isAppInventoryAllowed ? AppInventoryOption.APP_GROUP : AppInventoryOption.APP_BUNDLE_ID);
    return defaultOption as TContentOption;
  };

  const [contentOption, setContentOption] = useState<TContentOption>(getDefaultContentOption());

  // App Inventory specific states - shared between TopBox and Items
  const [appInventoryType, setAppInventoryType] = useState<AppInventoryType>(AppInventoryType.APP_NAME);
  const [appNameComparisonType, setAppNameComparisonType] = useState<ComparisonType>(ComparisonType.EQUALS);
  const [bundleIdComparisonType, setBundleIdComparisonType] = useState<ComparisonType>(ComparisonType.EQUALS);

  const initAppGroupComparisonType = deriveComparisonType(
    initState[InventoryContentOption.APP_GROUP]?.val as ComparisonMap | undefined
  );
  const initBundleIdComparisonType = deriveBundleIdComparisonType(
    initState[InventoryContentOption.APP_BUNDLE_ID]?.val as ComparisonMap | undefined,
    initState[InventoryContentOption.APP_GROUP]?.val as ComparisonMap | undefined
  );

  useEffect(() => {
    setAppNameComparisonType(initAppGroupComparisonType);
    setBundleIdComparisonType(initBundleIdComparisonType);
  }, [initAppGroupComparisonType, initBundleIdComparisonType]);

  // Update contentOption when feature flag changes (e.g., GrowthBook loads)
  // This handles the case where APP_BUNDLE_ID was set before GrowthBook loaded
  useEffect(() => {
    if (isAppInventoryAllowed && contentOption === AppInventoryOption.APP_BUNDLE_ID) {
      setContentOption(AppInventoryOption.APP_GROUP as TContentOption);
    }
  }, [isAppInventoryAllowed, contentOption]);

  return {
    setAppNameComparisonType,
    setAppInventoryType,
    setContentOption,
    setBundleIdComparisonType,
    isAppInventoryAllowed,
    contentOption,
    appInventoryType,
    appNameComparisonType,
    bundleIdComparisonType,
  };
};
